import { useState } from 'react';

import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import MarkEmailUnreadRoundedIcon from '@mui/icons-material/MarkEmailUnreadRounded';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import { useToast } from '@/components/common/toast-context';
import { useResendVerification } from '@/features/auth/useSession';

interface VerifyEmailBannerProps {
  email: string;
}

/**
 * Spec §5: unverified users can read but not post, comment or vote — and the
 * UI has to say so, dismissibly, with a way to resend.
 *
 * Laid out as a small card rather than a strip of alert. MUI's `action` slot
 * puts everything on the message's own row, so on a phone the sentence was
 * squeezed into a narrow column beside a button and a cross — three things
 * fighting over 360px. Only the cross lives there now; the resend button sits
 * under the text on a phone and beside it on a wider screen, where there is
 * room for it.
 *
 * Outlined rather than filled, too. This is a reminder about something the
 * person has probably already done and is waiting on, not a warning that
 * anything is wrong, and a solid amber bar across the top of every page reads
 * as the second.
 *
 * The address is shown because the commonest reason for not receiving the mail
 * is a typo in it, and somebody who cannot see what they typed cannot notice.
 *
 * Dismissal is per-session on purpose: it lives in component state rather than
 * localStorage, so the reminder comes back next visit while the account is
 * still unverified.
 */
export function VerifyEmailBanner({ email }: VerifyEmailBannerProps) {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState(false);
  const resend = useResendVerification();
  const { showToast } = useToast();

  const handleResend = (): void => {
    resend.mutate(email, {
      onSuccess: (result) => showToast(result.message, 'success'),
      onError: () => showToast(t('auth.verify.resendFailed'), 'error'),
    });
  };

  return (
    // unmountOnExit: a collapsed Collapse keeps its children in the DOM, where
    // they stay focusable and readable by a screen reader. A dismissed banner
    // has to actually go away, not just become 0px tall.
    <Collapse in={!dismissed} unmountOnExit>
      <Alert
        severity="warning"
        variant="outlined"
        icon={<MarkEmailUnreadRoundedIcon fontSize="small" />}
        // Only the dismiss control here. MUI's `action` replaces the built-in
        // close button rather than sitting beside it, and its label names what
        // it dismisses — the default "Close" tells a screen-reader user
        // nothing.
        action={
          <IconButton
            color="inherit"
            size="small"
            onClick={() => setDismissed(true)}
            aria-label={t('auth.verify.dismiss')}
          >
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        }
        sx={{
          mb: 2,
          alignItems: 'flex-start',
          // A tint of the warning colour rather than the filled variant's
          // solid block, so it reads as a note rather than an error.
          bgcolor: 'warning.main',
          borderColor: 'warning.main',
          '&, & .MuiAlert-icon': { color: 'text.primary' },
          backgroundImage: 'none',
          // 12% in either mode: enough to separate it from the page, not
          // enough to shout across it.
          '--tgbi-tint': '12%',
          background: (theme) =>
            `color-mix(in oklab, ${theme.palette.warning.main} var(--tgbi-tint), transparent)`,
          '& .MuiAlert-action': { pt: 0.25, mr: -0.5 },
          '& .MuiAlert-message': { width: '100%', minWidth: 0 },
        }}
      >
        <AlertTitle sx={{ mb: 0.25, fontWeight: 600 }}>{t('auth.verify.title')}</AlertTitle>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 1.25, sm: 2 }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
        >
          <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>
            {t('auth.verify.banner')}{' '}
            {/* Wraps anywhere rather than pushing the card wider than the
                screen — a long address on a narrow phone otherwise scrolls
                the whole page sideways. */}
            <Typography
              component="span"
              variant="body2"
              sx={{ fontWeight: 600, wordBreak: 'break-all' }}
            >
              {email}
            </Typography>
          </Typography>

          <Button
            variant="outlined"
            color="inherit"
            size="small"
            onClick={handleResend}
            disabled={resend.isPending}
            sx={{ flexShrink: 0, alignSelf: { xs: 'stretch', sm: 'center' } }}
          >
            {resend.isPending ? t('common.sending') : t('auth.verify.resend')}
          </Button>
        </Stack>
      </Alert>
    </Collapse>
  );
}
